import { takeLatest, takeEvery, select, put, call } from 'redux-saga/effects'
import { push, replace } from 'connected-react-router'
import { SubmissionError } from 'redux-form'
import { stringifyURLParams } from '@offerzen/pinata-shame'
import { interviewRequestEntities } from '@/core/reducks'
import { fetchInterviewRequestEntitiesSaga, updateInterviewRequestEntitySaga } from '@/core/sagas/hiringTeamInterviewRequests'
import { fetchHiringTeamMemberProfileEntitiesSaga } from '@/core/sagas/hiringTeamMemberProfiles'
import { fetchPositionEntitiesSaga } from '@/core/sagas/positions'
import { memberProfileId, hiringTeamId } from '@/core/selectors/currentUser'
import { errorHandler } from '@/core/helpers/errorHelpers'

import {
  getUnavailableCandidateNotifications,
  updateUnavailableCandidateNotifications,
} from '@/core/apiModules/unavailableCandidateNotifications'
import { showSystemError, hideSystemError } from '@/core/components/SystemErrorRedux'

import { interviewRequestStatusList } from '../helpers/interviewRequestHelpers'
import { interviewRequestListView } from '../reducks'
import { interviewRequestListFilters } from '../selectors/interviewRequestList'

export function* initFiltersSaga() {
  try {
    yield put(hideSystemError())

    // Push the member profile ID to the URL params if not present
    const filters = yield select(interviewRequestListFilters)

    if (filters.member_profile_id) {
      // If initial filters are set, trigger fetch of the list
      yield put(interviewRequestListView.fetchInterviewRequests())
      return
    }

    // If initial filters are not set, apply them
    const currentMember = yield select(memberProfileId)
    yield put(
      interviewRequestListView.applyFilter(
        {
          ...filters,
          member_profile_id: currentMember,
        },
        true,
      ),
    )
  } catch (err) {
    const { error } = yield call(errorHandler, err)
    yield put(showSystemError(error))
  }
}

export function* applyFilterSaga({ meta }) {
  try {
    yield put(hideSystemError())

    const filters = yield select(interviewRequestListFilters)

    // Execute the desired action over the history
    if (meta.replace) {
      yield put(replace({ search: stringifyURLParams({ ...filters, ...meta.filters }) }))
    } else {
      yield put(push({ search: stringifyURLParams({ ...filters, ...meta.filters }) }))
    }

    // Trigger fetch of the list
    yield put(interviewRequestListView.fetchInterviewRequests())
  } catch (err) {
    const { error } = yield call(errorHandler, err)
    yield put(showSystemError(error))
  }
}

export function* applyFilterFormSaga({ meta }) {
  try {
    const filters = yield select(interviewRequestListFilters)
    yield put(push({ search: stringifyURLParams({ ...filters, ...meta.filters }) }))

    // Resolve form
    yield call(meta.formResolve)

    // Trigger fetch of the list
    yield put(interviewRequestListView.fetchInterviewRequests())
  } catch (err) {
    const { error } = yield call(errorHandler, err, "Something went wrong. We couldn't fetch ")
    yield call(meta.formReject, new SubmissionError({ _error: error }))
  }
}

export function* clearFiltersSaga() {
  try {
    yield put(hideSystemError())

    const filters = yield select(interviewRequestListFilters)
    const currentMember = yield select(memberProfileId)
    yield put(
      interviewRequestListView.applyFilter({
        ...filters,
        member_profile_id: currentMember,
        status: undefined,
        position_ids: [],
        order_by: undefined,
        order_direction: undefined,
        page: undefined,
        messages: 'all',
        candidate_name: undefined,
      }),
    )
  } catch (err) {
    const { error } = yield call(errorHandler, err)
    yield put(showSystemError(error))
  }
}

export function* toggleArchivedSaga({ meta }) {
  try {
    yield put(hideSystemError())

    const currentMember = yield select(memberProfileId)
    yield put(
      interviewRequestListView.applyFilter({
        member_profile_id: currentMember,
        status: undefined,
        position_ids: [],
        order_by: undefined,
        order_direction: undefined,
        page: undefined,
        messages: 'all',
        archived: meta.archived,
      }),
    )
  } catch (err) {
    const { error } = yield call(errorHandler, err)
    yield put(showSystemError(error))
  }
}

export function* fetchPositionsSaga({ meta }) {
  try {
    yield put(hideSystemError())

    const hiringTeam = yield select(hiringTeamId)
    const response = yield call(fetchPositionEntitiesSaga, hiringTeam, meta.params)
    yield put(interviewRequestListView.fetchPositionsSuccess(response))
  } catch (err) {
    const { error } = yield call(errorHandler, err)
    yield put(interviewRequestListView.fetchPositionsError(error))
    yield put(showSystemError(error))
  }
}

export function* fetchMembersSaga({ meta }) {
  try {
    yield put(hideSystemError())

    const hiringTeam = yield select(hiringTeamId)
    const response = yield call(fetchHiringTeamMemberProfileEntitiesSaga, hiringTeam, meta.params)
    yield put(interviewRequestListView.fetchMembersSuccess(response))
  } catch (err) {
    const { error } = yield call(errorHandler, err)
    yield put(interviewRequestListView.fetchMembersError(error))
    yield put(showSystemError(error))
  }
}

export function* fetchInterviewRequestsSaga({ meta }) {
  try {
    yield put(hideSystemError())

    const hiringTeam = yield select(hiringTeamId)
    const filters = yield select(interviewRequestListFilters)

    // Calculate if we have to send status OR ats_status
    const selectedStatus = interviewRequestStatusList.find(status => status.id === filters.status)
    const params = {
      ...meta.params,
      ...filters,
      member_profile_id: filters.member_profile_id === 'all' ? undefined : filters.member_profile_id,
      archived: filters.archived === true,
      ...(selectedStatus
        ? {
            ats_status: 'unassigned',
          }
        : {
            status: undefined,
            ats_status: filters.status,
          }),
    }

    const response = yield call(fetchInterviewRequestEntitiesSaga, hiringTeam, params)
    yield put(interviewRequestListView.fetchInterviewRequestsSuccess(response))
  } catch (err) {
    const { error } = yield call(errorHandler, err)
    yield put(interviewRequestListView.fetchInterviewRequestsError(error))
    yield put(showSystemError(error))
  }
}

export function* updateInterviewRequestSaga({ meta }) {
  try {
    yield put(hideSystemError())

    // Fake the update first so users don't see delay
    yield put(interviewRequestEntities.updateItem(meta.interviewRequestId, meta.data))

    const hiringTeam = yield select(hiringTeamId)
    const response = yield call(updateInterviewRequestEntitySaga, hiringTeam, meta.interviewRequestId, meta.data)
    yield put(interviewRequestListView.updateInterviewRequestSuccess(meta.interviewRequestId, response))
  } catch (err) {
    // Rollback the changes we faked
    yield put(interviewRequestEntities.updateItem(meta.interviewRequestId, meta.previousData))

    const { error } = yield call(errorHandler, err)
    yield put(interviewRequestListView.updateInterviewRequestError(meta.interviewRequestId, error))
    yield put(showSystemError(error))
  }
}

export function* archiveInterviewRequestSaga({ meta }) {
  try {
    yield put(hideSystemError())

    const hiringTeam = yield select(hiringTeamId)
    const response = yield call(updateInterviewRequestEntitySaga, hiringTeam, meta.interviewRequestId, meta.data)
    yield put(interviewRequestListView.archiveInterviewRequestSuccess(meta.interviewRequestId, response))
  } catch (err) {
    const { error } = yield call(errorHandler, err)
    yield put(interviewRequestListView.archiveInterviewRequestError(meta.interviewRequestId, error))
    yield put(showSystemError(error))
  }
}

export function* withdrawInterviewRequestSaga({ meta }) {
  try {
    const hiringTeam = yield select(hiringTeamId)
    const response = yield call(updateInterviewRequestEntitySaga, hiringTeam, meta.interviewRequestId, { status: 'withdrawn' })
    yield put(interviewRequestListView.withdrawInterviewRequestSuccess(meta.interviewRequestId, response))
    yield call(meta.formResolve)
  } catch (err) {
    const { error } = yield call(errorHandler, err, "Something went wrong. We couldn't withdraw the request.")
    yield put(interviewRequestListView.withdrawInterviewRequestError(meta.interviewRequestId, error))
    yield call(meta.formReject, new SubmissionError({ _error: error }))
  }
}

export function* fetchNotifications() {
  try {
    // all hiring teams / AM impersonation complexity is handled by the back end
    const currentMember = yield select(memberProfileId)
    const response = yield call(getUnavailableCandidateNotifications, currentMember)
    yield put(interviewRequestListView.fetchUnavailableCandidateNotificationsSuccess(response))
  } catch (err) {
    const { error } = yield call(errorHandler, err)
    yield put(interviewRequestListView.fetchUnavailableCandidateNotificationsError(error))
  }
}

export function* dismissNotifications({ meta }) {
  try {
    // all hiring teams / AM impersonation complexity is handled by the back end
    const countForSeenNotification =
      meta.unavailable_reason === 'candidate_placed' ? { candidatePlacedCount: 0 } : { automaticallyArchivedCount: 0 }
    yield put(interviewRequestListView.resetUnavailableCandidateNotificationCount(countForSeenNotification))
    const currentMember = yield select(memberProfileId)
    yield call(updateUnavailableCandidateNotifications, currentMember, meta)
  } catch (err) {
    yield call(errorHandler, err)
  }
}

export default function* watchInterviewRequestListSaga() {
  yield takeLatest(interviewRequestListView.INIT_FILTERS, initFiltersSaga)
  yield takeLatest(interviewRequestListView.APPLY_FILTER, applyFilterSaga)
  yield takeLatest(interviewRequestListView.APPLY_FILTER_FORM, applyFilterFormSaga)
  yield takeLatest(interviewRequestListView.CLEAR_FILTERS, clearFiltersSaga)
  yield takeLatest(interviewRequestListView.TOGGLE_ARCHIVED, toggleArchivedSaga)

  yield takeLatest(interviewRequestListView.FETCH_POSITIONS, fetchPositionsSaga)
  yield takeLatest(interviewRequestListView.FETCH_MEMBERS, fetchMembersSaga)
  yield takeLatest(interviewRequestListView.FETCH_INTERVIEW_REQUESTS, fetchInterviewRequestsSaga)

  yield takeEvery(interviewRequestListView.UPDATE_INTERVIEW_REQUEST, updateInterviewRequestSaga)
  yield takeEvery(interviewRequestListView.ARCHIVE_INTERVIEW_REQUEST, archiveInterviewRequestSaga)
  yield takeLatest(interviewRequestListView.WITHDRAW_INTERVIEW_REQUEST, withdrawInterviewRequestSaga)

  yield takeLatest(interviewRequestListView.FETCH_UNAVAILABLE_CANDIDATE_NOTIFICATIONS, fetchNotifications)
  yield takeLatest(interviewRequestListView.DISMISS_UNAVAILABLE_CANDIDATE_NOTIFICATIONS, dismissNotifications)
}
